import React, { useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import MainLayout from '../../components/MainLayout';
import { useTranslation } from '../../context/LanguageContext';
import StepNav from './StepNav';
import ProductSelector from './ProductSelector';
import {
  defectThreshold,
  defectTypeMeta,
  contributionMeta,
  defectAnnotations,
  kpiPanel,
  judgmentBadgeMeta,
  judgmentHistory,
  getProduct,
  defaultDrilldownShotId,
} from '../../mock/moduleb';
import './moduleb-common.css';
import './ModuleBJudgment.css';

const KPI_CARDS = [
  { key: 'f1', labelKey: 'pages.moduleb.judgment.kpi.f1', icon: 'target', accent: '#22c55e', value: kpiPanel.overallF1, unit: '%' },
  { key: 'recall', labelKey: 'pages.moduleb.judgment.kpi.recall', icon: 'trending_up', accent: '#f97316', value: `+${kpiPanel.tempRecallGain}`, unit: '%p' },
  { key: 'total', labelKey: 'pages.moduleb.judgment.kpi.total', icon: 'inventory', accent: '#3B82F6', value: kpiPanel.totalJudged.toLocaleString(), unit: '' },
  { key: 'rate', labelKey: 'pages.moduleb.judgment.kpi.rate', icon: 'percent', accent: '#ef4444', value: kpiPanel.defectRate, unit: '%' },
];

function ModuleBJudgment() {
  const { t } = useTranslation();
  const [searchParams, setSearchParams] = useSearchParams();
  const shotId = searchParams.get('shot') || defaultDrilldownShotId;
  const product = getProduct(shotId);
  const [selected, setSelected] = useState(shotId);

  const badge = judgmentBadgeMeta[product.result];

  // 제품 카탈로그 → 화면 데이터로 조립
  const defectTypes = defectTypeMeta.map((m) => {
    const prob = product.defects[m.key] ?? 0;
    return { ...m, prob, thresholdOver: prob >= defectThreshold };
  });
  const contribution = Object.entries(product.contribution)
    .map(([source, percent]) => ({ source, percent, ...contributionMeta[source] }))
    .sort((a, b) => b.percent - a.percent);
  const topContrib = contribution[0];

  return (
    <MainLayout>
      <div className="mb-container">
        <StepNav current={4} shotId={shotId} />

        <div className="mb-page-header">
          <div>
            <div className="mb-page-title">
              <span className="mb-step-tag">STEP 4</span>{t('pages.moduleb.nav.steps.judgment.title')}
              <span className="mb-product-chip">
                <span className="material-symbols-outlined">inventory_2</span>
                {product.productId}
              </span>
            </div>
            <div className="mb-page-subtitle">
              {t('pages.moduleb.judgment.subtitle')}
            </div>
          </div>
          <ProductSelector value={shotId} onChange={(id) => { setSearchParams({ shot: id }); setSelected(id); }} />
        </div>

        <div className="mb-body">
          {/* 상단: 최종 판정 + KPI */}
          <div className="judg-top">
            {/* 최종 판정 배지 */}
            <div className="mb-card judg-verdict-card" style={{ borderColor: badge.color }}>
              <div className="judg-verdict-product">
                <span className="material-symbols-outlined">inventory_2</span>
                <strong>{product.productId}</strong> <span className="judg-verdict-shot">({shotId})</span>
              </div>
              <div className="judg-verdict-badge" style={{ color: badge.color, background: badge.bg }}>
                <span className="material-symbols-outlined">
                  {product.result === 'NG' ? 'cancel' : 'check_circle'}
                </span>
                {badge.label}
              </div>
              <div className="judg-verdict-metrics">
                <div className="judg-metric">
                  <span className="judg-metric-label">{t('pages.moduleb.judgment.fusionScore')}</span>
                  <span className="judg-metric-value" style={{ color: badge.color }}>{product.fusionScore.toFixed(2)}</span>
                </div>
                <div className="judg-metric-divider" />
                <div className="judg-metric">
                  <span className="judg-metric-label">{t('pages.moduleb.judgment.confidence')}</span>
                  <span className="judg-metric-value">{product.confidence}<small>%</small></span>
                </div>
              </div>
              <div className="judg-threshold-line">
                {t('pages.moduleb.judgment.threshold', { value: defectThreshold.toFixed(2) })} {product.result === 'NG' ? t('pages.moduleb.judgment.thresholdOver') : t('pages.moduleb.judgment.thresholdUnder')}
              </div>
            </div>

            {/* KPI 4개 */}
            <div className="judg-kpi-grid">
              {KPI_CARDS.map((c) => (
                <div key={c.key} className="mb-stat-card" style={{ '--mb-accent': c.accent }}>
                  <div className="mb-stat-label">
                    <span className="material-symbols-outlined" style={{ color: c.accent }}>{c.icon}</span>
                    {t(c.labelKey)}
                  </div>
                  <div className="mb-stat-value">
                    {c.value}{c.unit && <span className="mb-stat-unit">{c.unit}</span>}
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* 중단: 불량유형 + 기여도 + 이미지 */}
          <div className="judg-mid">
            {/* 불량 유형 분류 */}
            <div className="mb-card">
              <div className="mb-card-title">
                <span className="material-symbols-outlined">category</span>{t('pages.moduleb.judgment.defectTypeTitle')}
              </div>
              <div className="judg-defect-list">
                {defectTypes.map((d) => (
                  <div key={d.key} className={`judg-defect-row ${d.thresholdOver ? 'over' : ''}`}>
                    <div className="judg-defect-name">
                      {t(d.labelKey)}
                      {d.tempSensitive && <span className="judg-temp-tag">{t('pages.moduleb.common.tempSensitive')}</span>}
                    </div>
                    <div className="mb-bar-track judg-defect-bar">
                      <div
                        className="mb-bar-fill"
                        style={{
                          width: `${d.prob * 100}%`,
                          background: d.thresholdOver ? '#ef4444' : d.prob >= 0.3 ? '#fbbf24' : '#4b5563',
                        }}
                      />
                      <div className="judg-defect-threshold" style={{ left: `${defectThreshold * 100}%` }} />
                    </div>
                    <div className={`judg-defect-prob ${d.thresholdOver ? 'over' : ''}`}>
                      {(d.prob * 100).toFixed(0)}%
                    </div>
                  </div>
                ))}
              </div>
              <div className="judg-defect-legend">
                <span><span className="judg-legend-line" /> {t('pages.moduleb.judgment.threshold', { value: defectThreshold * 100 + '%' })}</span>
                <span className="judg-legend-over">■ {t('pages.moduleb.judgment.legendOver')}</span>
              </div>
            </div>

            {/* 판정 근거 (기여 모달리티) */}
            <div className="mb-card">
              <div className="mb-card-title">
                <span className="material-symbols-outlined">insights</span>{t('pages.moduleb.judgment.contribTitle')}
              </div>
              <div className="judg-contrib-stack">
                {contribution.map((c) => (
                  <div
                    key={c.source}
                    className="judg-contrib-seg"
                    style={{ width: `${c.percent}%`, background: c.color }}
                    title={`${t(c.labelKey)} ${c.percent}%`}
                  >
                    {c.percent >= 15 && `${c.percent}%`}
                  </div>
                ))}
              </div>
              <div className="judg-contrib-list">
                {contribution.map((c) => (
                  <div key={c.source} className="judg-contrib-item">
                    <span className="fusion-contrib-chip" style={{ background: c.color }} />
                    <span className="judg-contrib-label">
                      {t(c.labelKey)}
                      {c.tempSensitive && c.source === topContrib.source && <span className="judg-temp-tag">{t('pages.moduleb.judgment.tempLed')}</span>}
                    </span>
                    <span className="judg-contrib-pct">{c.percent}%</span>
                  </div>
                ))}
              </div>
              <div className="judg-contrib-note">
                <span className="material-symbols-outlined">priority_high</span>
                <strong>{t('pages.moduleb.judgment.noteHead', { label: t(topContrib.labelKey), percent: topContrib.percent })}</strong>{' '}
                {topContrib.source === 'thermal'
                  ? t('pages.moduleb.judgment.noteThermal')
                  : t('pages.moduleb.judgment.noteOther', { label: t(topContrib.labelKey) })}
              </div>
            </div>

            {/* 제품 이미지 + 결함 어노테이션 */}
            <div className="mb-card">
              <div className="mb-card-title">
                <span className="material-symbols-outlined">crop_free</span>{t('pages.moduleb.judgment.annotationTitle')}
              </div>
              <div className="judg-image">
                <div className="judg-image-placeholder">
                  <span className="material-symbols-outlined">deployed_code</span>
                  {t('pages.moduleb.judgment.productImage')} · {product.productId}
                </div>
                {product.result === 'NG' && defectAnnotations.map((a) => (
                  <div
                    key={a.id}
                    className="judg-annot"
                    style={{ left: `${a.x}%`, top: `${a.y}%`, width: `${a.w}%`, height: `${a.h}%`, borderColor: a.color }}
                  >
                    <span className="judg-annot-label" style={{ background: a.color }}>{a.label}</span>
                  </div>
                ))}
              </div>
              <div className="judg-image-caption">
                {product.result === 'NG' ? t('pages.moduleb.judgment.annotationNg') : t('pages.moduleb.judgment.annotationOk')}
              </div>
            </div>
          </div>

          {/* 하단: 최근 판정 이력 */}
          <div className="mb-card mb-mt" style={{ padding: '16px 16px 8px' }}>
            <div className="mb-card-title">
              <span className="material-symbols-outlined">history</span>{t('pages.moduleb.judgment.historyTitle')}
            </div>
            <div className="mb-table-wrap" style={{ maxHeight: 220 }}>
              <table className="mb-table">
                <thead>
                  <tr>
                    <th>{t('pages.moduleb.common.shotId')}</th>
                    <th>{t('pages.moduleb.common.productId')}</th>
                    <th>{t('pages.moduleb.common.time')}</th>
                    <th style={{ textAlign: 'right' }}>{t('pages.moduleb.judgment.fusionScore')}</th>
                    <th>{t('pages.moduleb.judgment.verdict')}</th>
                    <th>{t('pages.moduleb.judgment.defectType')}</th>
                    <th>{t('pages.moduleb.judgment.contribModality')}</th>
                  </tr>
                </thead>
                <tbody>
                  {judgmentHistory.map((r) => {
                    const b = judgmentBadgeMeta[r.result];
                    return (
                      <tr
                        key={r.shotId}
                        className={selected === r.shotId ? 'selected' : ''}
                        onClick={() => setSelected(r.shotId)}
                        style={{ cursor: 'pointer' }}
                      >
                        <td className="mb-mono">{r.shotId}</td>
                        <td className="mb-mono">{r.productId}</td>
                        <td className="mb-mono">{r.ts}</td>
                        <td className="mb-mono" style={{ textAlign: 'right', color: r.result === 'NG' ? '#ef4444' : '#22c55e' }}>{r.score.toFixed(2)}</td>
                        <td><span className="mb-badge" style={{ color: b.color, background: b.bg }}>{r.result}</span></td>
                        <td>{r.defectKey ? t(`pages.moduleb.defects.${r.defectKey}`) : '-'}</td>
                        <td style={{ color: r.contribKey === 'thermal' ? '#f97316' : '#9CA3AF' }}>{r.contribKey ? t(contributionMeta[r.contribKey].labelKey) : '-'}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
    </MainLayout>
  );
}

export default ModuleBJudgment;
